import { execSync, spawn } from 'child_process';
import { existsSync, readFileSync, writeFileSync } from 'fs';
import { join } from 'path';
import { logger } from './logger.js';

// ============================================================================
// Types
// ============================================================================

export interface WslStatus {
  installed: boolean;
  version: number | null;
  defaultDistro: string | null;
}

export interface ToolStatus {
  installed: boolean;
  version: string | null;
}

export interface InstallResult {
  success: boolean;
  version?: string;
  error?: string;
  skipped?: boolean;
}

// ============================================================================
// WSL helpers
// ============================================================================

/**
 * Execute a command inside WSL and return trimmed stdout, or null on failure.
 */
export function wslExec(command: string, timeout = 10000): string | null {
  try {
    const output = execSync(`wsl -e bash -c "${command.replace(/"/g, '\\"')}"`, {
      encoding: 'utf-8',
      timeout,
      stdio: ['pipe', 'pipe', 'pipe'],
    });
    // Strip WSL relay errors
    return output
      .split('\n')
      .filter(l => !l.includes('WSL') || !l.includes('ERROR'))
      .join('\n')
      .trim();
  } catch {
    return null;
  }
}

/**
 * Execute a long-running install command in WSL. Returns stdout on success, null on failure.
 */
function wslInstall(command: string, timeout = 120000): string | null {
  try {
    const output = execSync(`wsl -e bash -c "${command.replace(/"/g, '\\"')}"`, {
      encoding: 'utf-8',
      timeout,
      stdio: ['pipe', 'pipe', 'pipe'],
    });
    return output.trim();
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    logger.debug(`wslInstall error: ${msg}`);
    return null;
  }
}

/**
 * Parse WSL --list --verbose output (handles UTF-16 LE encoding on Windows).
 */
function parseWslList(raw: string): string[] {
  return raw
    .replace(/\0/g, '')          // strip null bytes from UTF-16
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.length > 0);
}

// ============================================================================
// WSL detection
// ============================================================================

export function checkWsl(): WslStatus {
  // Check if wsl command exists
  try {
    const statusRaw = execSync('wsl --status', {
      encoding: 'utf-8',
      timeout: 10000,
      stdio: ['pipe', 'pipe', 'pipe'],
    }).replace(/\0/g, '');

    let version: number | null = null;
    const versionMatch = statusRaw.match(/(\d)\s*$/m) || statusRaw.match(/version.*?:\s*(\d)/i);
    if (versionMatch) version = parseInt(versionMatch[1], 10);

    // Get default distro
    let defaultDistro: string | null = null;
    try {
      const listRaw = execSync('wsl --list --verbose', {
        encoding: 'utf-8',
        timeout: 10000,
        stdio: ['pipe', 'pipe', 'pipe'],
      });
      const lines = parseWslList(listRaw);
      for (const line of lines) {
        if (line.startsWith('*')) {
          // * Ubuntu   Running   2
          const parts = line.substring(1).trim().split(/\s+/);
          defaultDistro = parts[0] || null;
          if (!version && parts.length >= 3) {
            version = parseInt(parts[parts.length - 1], 10);
          }
          break;
        }
      }
    } catch {
      // list failed but status worked = WSL installed with no distro
    }

    return { installed: true, version: version || 2, defaultDistro };
  } catch {
    return { installed: false, version: null, defaultDistro: null };
  }
}

// ============================================================================
// Tool checks
// ============================================================================

export function checkGitInWsl(): ToolStatus {
  const out = wslExec('git --version 2>/dev/null');
  if (!out) return { installed: false, version: null };
  const match = out.match(/(\d+\.\d+\.\d+)/);
  return { installed: true, version: match ? match[1] : out };
}

export function checkTmuxInWsl(): ToolStatus {
  const out = wslExec('tmux -V 2>/dev/null');
  if (!out) return { installed: false, version: null };
  const match = out.match(/(\d+\.\d+\w?)/);
  return { installed: true, version: match ? match[1] : out };
}

export function checkNvmInWsl(): ToolStatus {
  const out = wslExec('source ~/.nvm/nvm.sh 2>/dev/null && nvm --version 2>/dev/null');
  if (!out) return { installed: false, version: null };
  const match = out.match(/(\d+\.\d+\.\d+)/);
  return { installed: true, version: match ? match[1] : out };
}

export function checkNodeInWsl(): ToolStatus {
  const out = wslExec('source ~/.nvm/nvm.sh 2>/dev/null; node --version 2>/dev/null');
  if (!out) return { installed: false, version: null };
  const match = out.match(/(v?\d+\.\d+\.\d+)/);
  return { installed: true, version: match ? match[1] : out };
}

export function checkDotnetInWsl(): ToolStatus {
  const out = wslExec('export PATH=$PATH:$HOME/.dotnet && dotnet --version 2>/dev/null');
  if (!out) return { installed: false, version: null };
  const match = out.match(/(\d+\.\d+\.\d+)/);
  return { installed: true, version: match ? match[1] : out };
}

export function checkClaudeInWsl(): ToolStatus {
  const out = wslExec('source ~/.nvm/nvm.sh 2>/dev/null; claude --version 2>/dev/null');
  if (!out) return { installed: false, version: null };
  const match = out.match(/(\d+\.\d+\.\d+)/);
  return { installed: true, version: match ? match[1] : out };
}

export function checkSmartStackInWsl(): ToolStatus {
  const out = wslExec('source ~/.nvm/nvm.sh 2>/dev/null; ss --version 2>/dev/null');
  if (!out) return { installed: false, version: null };
  const match = out.match(/(\d+\.\d+\.\d+)/);
  return { installed: true, version: match ? match[1] : out };
}

// ============================================================================
// Installers
// ============================================================================

export async function installGitInWsl(): Promise<InstallResult> {
  const status = checkGitInWsl();
  if (status.installed) return { success: true, version: status.version!, skipped: true };

  const spinner = logger.spinner('Installing git...');
  const result = wslInstall('sudo apt-get update -qq && sudo apt-get install -y -qq git', 60000);
  if (!result && result !== '') {
    spinner.fail('Failed to install git');
    return { success: false, error: 'apt-get install git failed' };
  }

  const check = checkGitInWsl();
  if (check.installed) {
    spinner.succeed(`git ${check.version} installed`);
    return { success: true, version: check.version! };
  }
  spinner.fail('git installation could not be verified');
  return { success: false, error: 'Verification failed after install' };
}

export async function installTmuxInWsl(): Promise<InstallResult> {
  const status = checkTmuxInWsl();
  if (status.installed) return { success: true, version: status.version!, skipped: true };

  const spinner = logger.spinner('Installing tmux...');
  const result = wslInstall('sudo apt-get update -qq && sudo apt-get install -y -qq tmux', 60000);
  if (!result && result !== '') {
    spinner.fail('Failed to install tmux');
    return { success: false, error: 'apt-get install tmux failed' };
  }

  const check = checkTmuxInWsl();
  if (check.installed) {
    spinner.succeed(`tmux ${check.version} installed`);
    return { success: true, version: check.version! };
  }
  spinner.fail('tmux installation could not be verified');
  return { success: false, error: 'Verification failed after install' };
}

export async function installNvmInWsl(): Promise<InstallResult> {
  const status = checkNvmInWsl();
  if (status.installed) return { success: true, version: status.version!, skipped: true };

  const spinner = logger.spinner('Installing nvm...');
  const result = wslInstall(
    'curl -o- https://raw.githubusercontent.com/nvm-sh/nvm/v0.40.1/install.sh | bash',
    60000,
  );
  if (result === null) {
    spinner.fail('Failed to install nvm');
    return { success: false, error: 'nvm install script failed' };
  }

  const check = checkNvmInWsl();
  if (check.installed) {
    spinner.succeed(`nvm ${check.version} installed`);
    return { success: true, version: check.version! };
  }
  spinner.fail('nvm installation could not be verified');
  return { success: false, error: 'Verification failed after install' };
}

export async function installNodeInWsl(): Promise<InstallResult> {
  const status = checkNodeInWsl();
  if (status.installed) return { success: true, version: status.version!, skipped: true };

  // nvm is required
  const nvmCheck = checkNvmInWsl();
  if (!nvmCheck.installed) {
    return { success: false, error: 'nvm is not installed (required for Node.js)' };
  }

  const spinner = logger.spinner('Installing Node.js LTS...');
  const result = wslInstall(
    'source ~/.nvm/nvm.sh && nvm install --lts && nvm alias default lts/*',
    120000,
  );
  if (result === null) {
    spinner.fail('Failed to install Node.js');
    return { success: false, error: 'nvm install --lts failed' };
  }

  const check = checkNodeInWsl();
  if (check.installed) {
    spinner.succeed(`Node.js ${check.version} installed`);
    return { success: true, version: check.version! };
  }
  spinner.fail('Node.js installation could not be verified');
  return { success: false, error: 'Verification failed after install' };
}

export async function installDotnetInWsl(): Promise<InstallResult> {
  const status = checkDotnetInWsl();
  if (status.installed) return { success: true, version: status.version!, skipped: true };

  const spinner = logger.spinner('Installing .NET SDK 10...');

  // Download and run the official install script
  const installCmd = [
    'wget -q https://dot.net/v1/dotnet-install.sh -O /tmp/dotnet-install.sh',
    'chmod +x /tmp/dotnet-install.sh',
    '/tmp/dotnet-install.sh --channel 10.0',
  ].join(' && ');
  const result = wslInstall(installCmd, 180000);
  if (result === null) {
    spinner.fail('Failed to install .NET SDK 10');
    return { success: false, error: 'dotnet-install.sh failed' };
  }

  // Ensure PATH and DOTNET_ROOT are persisted in bashrc
  wslInstall(
    'grep -q .dotnet ~/.bashrc || echo \'export DOTNET_ROOT="$HOME/.dotnet"\nexport PATH="$PATH:$HOME/.dotnet:$HOME/.dotnet/tools"\' >> ~/.bashrc',
    5000,
  );

  const check = checkDotnetInWsl();
  if (check.installed) {
    spinner.succeed(`.NET SDK ${check.version} installed`);
    return { success: true, version: check.version! };
  }
  spinner.fail('.NET SDK installation could not be verified');
  return { success: false, error: 'Verification failed after install' };
}

export async function installClaudeInWsl(): Promise<InstallResult> {
  const status = checkClaudeInWsl();
  if (status.installed) return { success: true, version: status.version!, skipped: true };

  // Native install via curl (no Node.js dependency)
  const spinner = logger.spinner('Installing Claude Code...');
  const result = wslInstall(
    'curl -fsSL https://claude.ai/install.sh | bash',
    180000,
  );
  if (result === null) {
    spinner.fail('Failed to install Claude Code');
    return { success: false, error: 'Claude Code native install failed (curl -fsSL https://claude.ai/install.sh | bash)' };
  }

  const check = checkClaudeInWsl();
  if (check.installed) {
    spinner.succeed(`Claude Code ${check.version} installed`);
    return { success: true, version: check.version! };
  }
  spinner.fail('Claude Code installation could not be verified');
  return { success: false, error: 'Verification failed after install' };
}

export async function installSmartStackInWsl(): Promise<InstallResult> {
  const status = checkSmartStackInWsl();
  if (status.installed) return { success: true, version: status.version!, skipped: true };

  // Node/npm required
  const nodeCheck = checkNodeInWsl();
  if (!nodeCheck.installed) {
    return { success: false, error: 'Node.js is not installed (required for SmartStack CLI)' };
  }

  const spinner = logger.spinner('Installing SmartStack CLI...');
  const result = wslInstall(
    'source ~/.nvm/nvm.sh && npm install -g @atlashub/smartstack-cli',
    180000,
  );
  if (result === null) {
    spinner.fail('Failed to install SmartStack CLI');
    return { success: false, error: 'npm install -g @atlashub/smartstack-cli failed' };
  }

  const check = checkSmartStackInWsl();
  if (check.installed) {
    spinner.succeed(`SmartStack CLI ${check.version} installed`);
    return { success: true, version: check.version! };
  }
  spinner.fail('SmartStack CLI installation could not be verified');
  return { success: false, error: 'Verification failed after install' };
}

// ============================================================================
// Startup script
// ============================================================================

/**
 * Install the SmartStack dev startup script in WSL (~/.local/bin/smartstack-dev).
 * This script creates a tmux session that auto-launches claude and provides a
 * second window for dotnet/general dev work.
 */
export function installStartupScript(): { success: boolean; error?: string } {
  const script = [
    '#!/bin/bash',
    'source ~/.nvm/nvm.sh 2>/dev/null',
    'export DOTNET_ROOT="$HOME/.dotnet"',
    'export PATH="$PATH:$HOME/.dotnet:$HOME/.dotnet/tools"',
    '',
    'if tmux has-session -t smartstack 2>/dev/null; then',
    '  tmux attach -t smartstack',
    'else',
    '  tmux new-session -d -s smartstack -n claude',
    '  tmux send-keys -t smartstack:claude "claude --dangerously-skip-permissions" Enter',
    '  tmux new-window -t smartstack -n dev',
    '  tmux select-window -t smartstack:claude',
    '  tmux attach -t smartstack',
    'fi',
  ].join('\n');

  const result = wslInstall(
    `mkdir -p ~/.local/bin && cat > ~/.local/bin/smartstack-dev << 'SCRIPT'\n${script}\nSCRIPT\nchmod +x ~/.local/bin/smartstack-dev`,
    10000,
  );

  if (result === null) {
    return { success: false, error: 'Failed to write startup script' };
  }

  // Ensure ~/.local/bin is in PATH
  wslInstall(
    'grep -q ".local/bin" ~/.bashrc || echo \'export PATH="$PATH:$HOME/.local/bin"\' >> ~/.bashrc',
    5000,
  );

  return { success: true };
}

// ============================================================================
// Windows Terminal
// ============================================================================

const SMARTSTACK_PROFILE_NAME = 'SmartStack Dev';
const SMARTSTACK_PROFILE_GUID = '{17ce5bfe-17ed-5f3a-ab15-5cd5baafed5b}';

export function checkWindowsTerminal(): ToolStatus {
  try {
    execSync('where wt', { encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'], timeout: 5000 });
    return { installed: true, version: 'installed' };
  } catch {
    return { installed: false, version: null };
  }
}

export async function installWindowsTerminal(): Promise<InstallResult> {
  const status = checkWindowsTerminal();
  if (status.installed) return { success: true, version: 'installed', skipped: true };

  const spinner = logger.spinner('Installing Windows Terminal via winget...');
  try {
    execSync('winget install Microsoft.WindowsTerminal --accept-source-agreements --accept-package-agreements', {
      encoding: 'utf-8',
      timeout: 120000,
      stdio: ['pipe', 'pipe', 'pipe'],
    });
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    // winget returns non-zero even on success sometimes (already installed, etc.)
    if (msg.includes('already installed') || msg.includes('No applicable update')) {
      spinner.succeed('Windows Terminal already installed');
      return { success: true, version: 'installed', skipped: true };
    }
    spinner.fail('Failed to install Windows Terminal');
    return { success: false, error: `winget install failed: ${msg.substring(0, 200)}` };
  }

  const check = checkWindowsTerminal();
  if (check.installed) {
    spinner.succeed('Windows Terminal installed');
    return { success: true, version: 'installed' };
  }
  spinner.fail('Windows Terminal installation could not be verified');
  return { success: false, error: 'Verification failed after install' };
}

/**
 * Find the Windows Terminal settings.json path.
 */
function getWtSettingsPath(): string | null {
  const localAppData = process.env.LOCALAPPDATA;
  if (!localAppData) return null;

  // Standard Windows Terminal
  const standardPath = join(localAppData, 'Packages', 'Microsoft.WindowsTerminal_8wekyb3d8bbwe', 'LocalState', 'settings.json');
  if (existsSync(standardPath)) return standardPath;

  // Preview version
  const previewPath = join(localAppData, 'Packages', 'Microsoft.WindowsTerminalPreview_8wekyb3d8bbwe', 'LocalState', 'settings.json');
  if (existsSync(previewPath)) return previewPath;

  // Unpackaged (winget/scoop install)
  const unpackagedPath = join(localAppData, 'Microsoft', 'Windows Terminal', 'settings.json');
  if (existsSync(unpackagedPath)) return unpackagedPath;

  return null;
}

/**
 * Configure Windows Terminal with a SmartStack Dev profile that launches tmux + claude in WSL.
 */
export function configureWindowsTerminal(): { success: boolean; error?: string; profileExists?: boolean } {
  const settingsPath = getWtSettingsPath();
  if (!settingsPath) {
    return { success: false, error: 'Windows Terminal settings.json not found. Launch Windows Terminal once to generate it.' };
  }

  try {
    const raw = readFileSync(settingsPath, 'utf-8');
    // Strip BOM if present and handle JSON with comments (WT uses JSONC)
    const cleaned = raw.replace(/^\uFEFF/, '').replace(/\/\/.*$/gm, '').replace(/\/\*[\s\S]*?\*\//g, '');
    const settings = JSON.parse(cleaned);

    if (!settings.profiles) settings.profiles = {};
    if (!settings.profiles.list) settings.profiles.list = [];

    // Check if profile already exists
    const existing = settings.profiles.list.find(
      (p: { name?: string; guid?: string }) => p.guid === SMARTSTACK_PROFILE_GUID || p.name === SMARTSTACK_PROFILE_NAME,
    );
    if (existing) {
      return { success: true, profileExists: true };
    }

    // Add SmartStack Dev profile
    const profile = {
      guid: SMARTSTACK_PROFILE_GUID,
      name: SMARTSTACK_PROFILE_NAME,
      commandline: 'wsl -e bash -l ~/.local/bin/smartstack-dev',
      icon: '\u26A1',
      colorScheme: 'One Half Dark',
      startingDirectory: '~',
    };

    settings.profiles.list.push(profile);

    // Backup original
    const backupPath = settingsPath + '.bak';
    writeFileSync(backupPath, raw, 'utf-8');

    // Write updated settings
    writeFileSync(settingsPath, JSON.stringify(settings, null, 4), 'utf-8');

    return { success: true };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    return { success: false, error: `Failed to update settings: ${msg}` };
  }
}

/**
 * Launch Windows Terminal with the SmartStack Dev profile.
 */
export function launchWindowsTerminal(): boolean {
  try {
    // Launch WT with the SmartStack profile, detached from current process
    spawn('wt', ['-p', SMARTSTACK_PROFILE_NAME], {
      detached: true,
      stdio: 'ignore',
    }).unref();
    return true;
  } catch {
    return false;
  }
}
